import { useState } from "react";
import { useTranslate } from "@refinedev/core";

import { AdminLogin } from "./AdminLogin";
import { FosterMagicLogin } from "./FosterMagicLogin";
import type { LoginMode } from "../../auth/loginRoute";
import "./foster-login.css";

// The bottom-left hero photo. Drop the real image at `frontend/public/foster-login-animals.png`
// (the designer's `assets/animals.png`); until then nothing is rendered (no broken-image glyph),
// since it's purely decorative. Served from public/ rather than a bundler import so a missing asset
// never breaks the dev server / build.
function AnimalsImage() {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      className="animals-image"
      src="/foster-login-animals.png?v=2"
      alt=""
      aria-hidden="true"
      onError={() => setFailed(true)}
    />
  );
}

// jmix-exit (login split): the unauthenticated entry point, route-driven. The Gate resolves the
// `mode` from the address bar — /aerie → admin password login, /login → foster magic-code login — and
// renders ONLY that form. There is deliberately NO cross-link between the two: /aerie is not
// advertised to the public foster portal (and vice versa); each is reached by its own URL. Deep-link
// canonicalisation still routes a logged-out visit to the right form. Set-password / reset is its own
// standalone page (jmix-7-3), not here.
//
// fix/foster-login-redesign: the foster mode renders the user-provided "pleeggezinnen-login-react"
// scene (brand + soft background shapes + hero photo + inline-SVG house/heart + the branded card +
// trust footer) around the magic-code card (FosterMagicLogin). Visual styling lives in
// foster-login.css, scoped to `.login-page`. Admin mode keeps the plain centered `.fawkes-auth` card.
export function LoginPage({
  mode,
  onAuthenticated,
}: {
  mode: LoginMode;
  onAuthenticated: () => void;
}) {
  const t = useTranslate();

  if (mode === "foster") {
    return (
      <main className="login-page">
        <div className="background-shape shape-top-right" />
        <div className="background-shape shape-bottom-right" />
        <div className="background-shape shape-left" />

        <svg className="decor decor-house" viewBox="0 0 120 120" aria-hidden="true">
          <path
            d="M20 58 60 22l40 36v38a8 8 0 0 1-8 8H28a8 8 0 0 1-8-8V58Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M60 84s-20-12-20-28c0-9 6-15 14-15 5 0 9 3 12 8 3-5 7-8 12-8 8 0 14 6 14 15 0 16-20 28-20 28Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <div className="decor decor-paw" aria-hidden="true">🐾</div>
        <div className="decor decor-leaf-left" aria-hidden="true">☘</div>
        <div className="decor decor-leaf-right" aria-hidden="true">☘</div>

        <header className="brand">
          <img className="brand-icon" src="/brand-paw-circle.svg" alt="" width={44} height={44} />
          <span>{t("shell.brand")}</span>
        </header>

        <AnimalsImage />

        <section className="login-layout">
          <FosterMagicLogin onAuthenticated={onAuthenticated} />
        </section>

        <div className="trust">
          <span aria-hidden="true">🛡️</span>
          <span>{t("auth.foster.trust")}</span>
        </div>
      </main>
    );
  }

  return (
    <main className="fawkes-auth">
      <div className="fawkes-auth__brand">{t("shell.brand")}</div>
      <AdminLogin onAuthenticated={onAuthenticated} />
    </main>
  );
}
